import React, { Component } from 'react';
import intlTelInput from 'intl-tel-input';
import throttle from 'lodash/throttle';
import { fallbackFunc } from '../../utils/utils';
import { createGetCountryIsoByIpAction } from '../../helpers/api';
import { NUMBER_FORMAT } from '../../constants/generic';

/**
 * @typedef {object} PhoneNumberValue
 * @property {string} [phonePrefix]
 * @property {string} [phoneSuffix]
 * @property {boolean} [valid]
 */

/**
 * @typedef {object} PhoneNumberProps
 * @property {string} [fallbackCountry='fr']
 * @property {string} [inputClass]
 * @property {(value: PhoneNumberValue) => any} [onChange]
 * @property {() => any} [onBlur]
 * @property {PhoneNumberValue|string} [value] - empty string means there is no value
 */
class PhoneNumber extends Component {
  constructor(props) {
    super(props);
    this.setCallbacks();
  }

  setCallbacks() {
    this.refSetter = (input) => {
      this.inputRef = input;
    };

    this.handleOnChange = throttle(() => {
      // intl-tel-input v28: methods that depend on utils throw if called
      // before the utils script has finished loading.
      if (!this.utilsReady) return;

      const number = this.telInstance.getNumber(NUMBER_FORMAT.E164);
      const country = this.telInstance.getSelectedCountryData();
      const valid = this.telInstance.isValidNumber();
      const dialCode = country?.dialCode || '';
      const phoneSuffix = dialCode ? number.substring(dialCode.length + 1) : '';
      const suffixFallback = this.inputRef?.value;

      this.currentValue = {
        phonePrefix: dialCode ? '+' + dialCode : '',
        phoneSuffix: phoneSuffix || suffixFallback,
        valid
      };


      this.props.onChange(this.currentValue);
    }, 300);

    this.handleOnBlur = () => {
      this.handleOnChange();
      this.props.onBlur();
    };
  }

  componentDidMount() {
    this.createTelInstance();
    this.componentPropsUpdated();
  }

  createTelInstance() {
    const options = {
      loadUtils: () => import('intl-tel-input/utils'),
      separateDialCode: false
    };

    const { fallbackCountry, value } = this.props;
    const { phoneSuffix, phonePrefix } = value || {};
    const noValue = !(phonePrefix && phoneSuffix);

    if (noValue) {
      options.initialCountry = 'auto';
      options.geoIpLookup = createGetCountryIsoByIpAction(fallbackCountry);
    }

    this.telInstance = intlTelInput(this.inputRef, options);

    this.telInstance.promise.then(() => {
      this.utilsReady = true;
      this.updateCurrentValue();
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.telInstance.destroy();
  }

  componentPropsUpdated(pp) {
    this.updateCurrentValue(pp);
  }

  updateCurrentValue(pp) {
    if (!this.utilsReady) return;
    if (this.valueUpdated(pp)) {
      const { value } = this.props;

      if (!this.numberIsEqual(value, this.currentValue)) {
        const phonePrefix = value.phonePrefix || '';
        const phoneSuffix = value.phoneSuffix || '';

        this.telInstance.setNumber(phonePrefix + phoneSuffix);
      }
    }
  }

  valueUpdated(pp) {
    const { value: prevValue } = pp || {};
    const { value: nextValue } = this.props;

    return !pp || !this.numberIsEqual(nextValue, prevValue);
  }

  numberIsEqual(aNumber = {}, bNumber = {}) {
    const { phonePrefix: aPrefix, phoneSuffix: aSuffix } = aNumber;
    const { phonePrefix: bPrefix, phoneSuffix: bSuffix } = bNumber;

    return aPrefix === bPrefix && aSuffix === bSuffix;
  }

  render() {
    return (
      <input
        className={this.props.inputClass}
        ref={this.refSetter}
        onChange={this.handleOnChange}
        onBlur={this.handleOnBlur}
        type="tel"
      />
    );
  }
}

PhoneNumber.defaultProps = {
  onChange: fallbackFunc,
  onBlur: fallbackFunc,
  fallbackCountry: 'fr'
};


export default PhoneNumber;
